import Component from '@glimmer/component';
import type Owner from '@ember/owner';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { concat } from '@ember/helper';
import { ensureReimaginedStylesLoaded } from 'tio-employee/utils/reimagined/ensure-styles';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import Drawer from 'tio-ui/components/drawer';
import Card from 'tio-ui/components/card';
import SectionHeader from 'tio-ui/components/section-header';
import SmartActionsSection from 'tio-employee/components/reimagined/smart-actions-section';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import PageHero from 'tio-ui/components/page-hero';
import GlassCard from 'tio-ui/components/glass-card';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import ExtractionSummary from 'tio-employee/components/observability/extraction-summary';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import { EXTRACTION_STATES } from 'tio-data/data/resources/observable-document.constants';
import { formatShortDate } from 'tio-ui/format';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhFileText,
  PhUploadSimple,
  PhCheckCircle,
  PhWarning,
  PhClock,
  PhShieldCheck,
  PhEye,
  PhCloudArrowUp,
  PhSparkle,
  PhRobot,
  PhDownloadSimple,
  PhArrowSquareOut,
  PhFolderOpen,
} from 'ember-phosphor-icons';

export interface DocumentCenterModel {
  observableDocuments: ObservableDocumentModel[];
}

interface DocumentCenterSignature {
  Args: {
    model: DocumentCenterModel;
    variant: 'production' | 'reimagined';
  };
}

interface Document {
  id: string;
  name: string;
  type: string;
  status: 'verified' | 'pending' | 'action-needed';
  uploadDate: string;
  statusLabel: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  'ObservableProvider.NSLDS': 'NSLDS File',
  'ObservableProvider.ACCOUNT_STATEMENT': 'Loan Statement',
  'ObservableProvider.ACCOUNT_TXN_HISTORY': 'Transaction History',
  'ObservableProvider.PROXY': 'Proxy Upload',
};

function mapExtractionStatus(state: keyof typeof EXTRACTION_STATES): Document['status'] {
  const level = EXTRACTION_STATES[state];
  if (!level) return 'pending';
  if (level >= EXTRACTION_STATES['ExtractionState.PROCESSED']) return 'verified';
  if (state === 'ExtractionState.FAILED' || state === 'ExtractionState.INVALID')
    return 'action-needed';
  return 'pending';
}

function mapExtractionLabel(state: keyof typeof EXTRACTION_STATES): string {
  switch (state) {
    case 'ExtractionState.PROCESSED':
      return 'Processed';
    case 'ExtractionState.DERELICT':
      return 'Processed';
    case 'ExtractionState.FAILED':
      return 'Failed';
    case 'ExtractionState.INVALID':
      return 'Invalid';
    case 'ExtractionState.PROCESSING':
      return 'Processing';
    case 'ExtractionState.DISPATCHED':
      return 'Processing';
    case 'ExtractionState.UPLOADED':
      return 'Uploaded';
    default:
      return 'Unknown';
  }
}

function mapObservableToDocument(doc: ObservableDocumentModel): Document {
  const filename = doc.observableAsset?.filename || 'Unknown Document';
  const provider = doc.provider || '';
  const type = PROVIDER_LABELS[provider] || 'Document';
  const status = mapExtractionStatus(doc.extractionState);
  const statusLabel = mapExtractionLabel(doc.extractionState);
  const uploadDate = doc.createdAt ? formatShortDate(doc.createdAt) : '';

  return {
    id: doc.id ?? '',
    name: filename,
    type,
    status,
    uploadDate,
    statusLabel,
  };
}

export default class DocumentCenter extends Component<DocumentCenterSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked selectedDocumentId: string | null = null;

  constructor(owner: Owner, args: DocumentCenterSignature['Args']) {
    super(owner, args);
    // Backs both the `/reimagined/*` prototype (@variant="reimagined") and
    // ReimaginedDocumentCenter, spliced onto the shipped
    // observability.upload route behind the beta toggle
    // (@variant="production"). Its CSS is route-scoped; the parent
    // reimagined route also calls this, so the production variant is the
    // one that actually needs it here — this call is a harmless no-op there.
    ensureReimaginedStylesLoaded();
  }

  get isReimagined() {
    return this.args.variant === 'reimagined';
  }

  get isDemoMode() {
    return this.isReimagined && this.reimaginedData.isDemoMode;
  }

  get selectedObservableDocument(): ObservableDocumentModel | undefined {
    if (!this.selectedDocumentId) return undefined;
    return this.args.model.observableDocuments.find((d) => d.id === this.selectedDocumentId);
  }

  get selectedDocument(): Document | undefined {
    if (!this.selectedDocumentId) return undefined;
    return this.documents.find((d) => d.id === this.selectedDocumentId);
  }

  get isViewerOpen() {
    return !!this.selectedDocumentId;
  }

  viewDocument = (docId: string) => {
    this.selectedDocumentId = docId;
  };

  closeViewer = () => {
    this.selectedDocumentId = null;
  };

  get documents(): Document[] {
    return this.args.model.observableDocuments.map(mapObservableToDocument);
  }

  get showLoanUploads() {
    return this.reimaginedData.showStudentLoanRepayment || this.reimaginedData.showPslf;
  }

  get documentsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    if (this.documents.length) return 'live';
    return 'placeholder';
  }

  get verifiedCount() {
    return this.documents.filter((d) => d.status === 'verified').length;
  }

  get pendingCount() {
    return this.documents.filter((d) => d.status === 'pending').length;
  }

  get actionNeededCount() {
    return this.documents.filter((d) => d.status === 'action-needed').length;
  }

  get smartActions(): SmartAction[] {
    return [
      {
        id: 'upload',
        title: 'Upload Document',
        description: 'Upload loan statements, tax forms, or other documents for verification.',
        icon: PhUploadSimple,
        priority: 'urgent',
        category: 'deadline',
        cta: 'Upload Now',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'review-pending',
        title: 'Review Pending',
        description: `You have ${this.pendingCount} document(s) awaiting review.`,
        icon: PhClock,
        priority: 'high',
        category: 'deadline',
        cta: 'Review Documents',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'connect-accounts',
        title: 'Connect Accounts',
        description: 'Instantly verify loan data by connecting your servicer accounts.',
        icon: PhShieldCheck,
        priority: 'medium',
        category: 'opportunity',
        cta: 'Connect Now',
        route: 'authenticated.reimagined.advisor',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12" data-test-document-center={{@variant}}>
      <PageHero
        @title="Document Center"
        @subtitle="Upload, verify, and manage your financial documents"
        @icon={{PhFileText}}
        @gradient="brand-soft"
        @decoration="circles"
      >
        <DataIndicator
          @source={{this.documentsSource}}
          @showBadge={{this.isReimagined}}
          @showBorder={{this.isReimagined}}
          @dataSourceId="document-stats"
        >
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <GlassCard
              @label="Verified"
              @value={{this.verifiedCount}}
              @accent="default"
              @subtext="documents approved"
            />
            <GlassCard
              @label="Pending"
              @value={{this.pendingCount}}
              @accent="default"
              @subtext="under review"
            />
            <GlassCard
              @label="Action Needed"
              @value={{this.actionNeededCount}}
              @accent="default"
              @subtext="require attention"
            />
          </div>
        </DataIndicator>
      </PageHero>

      {{#if this.isReimagined}}
        <SmartActionsSection
          @actions={{this.smartActions}}
          @title="Quick Actions"
          @subtitle="Manage your documents"
        />
      {{/if}}

      <SectionHeader
        @icon={{PhFileText}}
        @title="Your Documents"
        @subtitle="All uploaded and verified documents"
        @iconClass={{if this.isReimagined "tio-fg-brand" "tio-fg-brand-accent"}}
      />
      <DataIndicator
        @source={{this.documentsSource}}
        @showBadge={{this.isReimagined}}
        @showBorder={{this.isReimagined}}
        @dataSourceId="document-list"
      >
        {{#if this.documents.length}}
          <div class="space-y-3">
            {{#each this.documents as |doc|}}
              <Card @padding="sm" class="tio-row-between hover:shadow-md transition-shadow">
                <div class="tio-row-loose">
                  <div
                    class="tio-row-center h-12 w-12 rounded-lg
                      {{if
                        (eq doc.status 'verified')
                        'tio-bg-success-soft'
                        (if (eq doc.status 'pending') 'tio-bg-warning-soft' 'tio-bg-danger-soft')
                      }}"
                  >
                    {{#if (eq doc.status "verified")}}
                      <PhCheckCircle class="tio-icon-lg tio-fg-success" />
                    {{else if (eq doc.status "pending")}}
                      <PhClock class="tio-icon-lg tio-fg-warning" />
                    {{else}}
                      <PhWarning class="tio-icon-lg tio-fg-danger" />
                    {{/if}}
                  </div>
                  <div>
                    <div class="font-medium tio-fg-primary">{{doc.name}}</div>
                    <div class="tio-row-tight mt-1">
                      <Text @size="xs" class="tio-fg-muted">{{doc.type}}</Text>
                      <span class="tio-fg-faint">·</span>
                      <Text @size="xs" class="tio-fg-muted">{{doc.uploadDate}}</Text>
                    </div>
                  </div>
                </div>
                <div class="tio-row">
                  <Chip
                    @intent={{if
                      (eq doc.status "verified")
                      "success"
                      (if (eq doc.status "pending") "warning" "danger")
                    }}
                  >
                    {{doc.statusLabel}}
                  </Chip>
                  <Button
                    @appearance="minimal"
                    @size="sm"
                    @icon={{PhEye}}
                    @ariaLabel={{concat "View document " doc.name}}
                    @testHook={{concat "document-center__view-" doc.id}}
                    {{on "click" (fn this.viewDocument doc.id)}}
                  />
                </div>
              </Card>
            {{/each}}
          </div>
        {{else}}
          <div
            class="tio-bg-surface rounded-xl border border-dashed p-12 text-center
              {{if this.isReimagined 'tio-border-strong' 'tio-border-default'}}"
          >
            <div class="tio-row-center h-16 w-16 rounded-full tio-bg-brand-soft mx-auto mb-4">
              <PhFolderOpen
                class="tio-icon-xl {{if this.isReimagined 'tio-fg-brand' 'tio-fg-brand-accent'}}"
              />
            </div>
            <Heading @level={{3}} class="mb-2">
              No Documents Yet
            </Heading>
            <Text @size="sm" class="tio-fg-muted mb-6 max-w-md mx-auto">
              Upload your loan statements, NSLDS file, or other documents to unlock employer
              contributions and track your progress.
            </Text>
            {{#if this.showLoanUploads}}
              <LinkTo
                @route="authenticated.observability.upload"
                @query={{hash
                  source="reimagined-documents"
                  providers="nslds,statement"
                  returnTo=(if
                    this.isReimagined
                    "authenticated.reimagined.documents"
                    "authenticated.observability.upload"
                  )
                }}
                class={{if
                  this.isReimagined
                  "tio-btn tio-btn--brand tio-btn--md"
                  "tio-inline-row gap-2 rounded-lg tio-bg-brand hover:tio-bg-brand-hover px-5 py-2.5 tio-text-sm-medium tio-fg-inverse shadow-sm transition-colors"
                }}
              >
                <PhUploadSimple class="tio-icon-sm" />
                Upload Documents
              </LinkTo>
            {{/if}}
          </div>
        {{/if}}
      </DataIndicator>

      <SectionHeader
        @icon={{PhCloudArrowUp}}
        @title="Upload Methods"
        @subtitle="Choose how to share your documents"
        @iconClass="tio-fg-info"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card @padding="lg" class="text-center">
          <div class="tio-row-center h-14 w-14 rounded-xl tio-bg-brand-soft mx-auto mb-4">
            <PhCloudArrowUp class="h-7 w-7 tio-fg-brand" />
          </div>
          <Heading @level={{4}} class="mb-2">File Upload</Heading>
          <Text @size="sm" class="tio-fg-secondary mb-4">
            Drag and drop or browse to upload PDF, JPG, or PNG documents.
          </Text>
          {{#if this.isReimagined}}
            <Button
              @intent="primary"
              @appearance="outlined"
              @testHook="document-center__upload-files"
              class="w-full"
            >
              Upload Files
            </Button>
          {{else}}
            <LinkTo
              @route="authenticated.observability.upload"
              @query={{hash
                source="reimagined-documents"
                providers="nslds,statement"
                returnTo="authenticated.observability.upload"
              }}
              class="block w-full rounded-md border tio-border-brand tio-bg-surface px-4 py-2 text-center tio-text-sm-medium tio-fg-brand shadow-sm hover:tio-bg-brand-wash focus:outline-none focus-visible:ring-2 focus-visible:tio-ring-brand focus-visible:ring-offset-2"
            >
              Upload Files
            </LinkTo>
          {{/if}}
        </Card>

        {{#if this.showLoanUploads}}
          <Card @padding="lg" class="text-center">
            <div class="tio-row-center h-14 w-14 rounded-xl tio-bg-info-soft mx-auto mb-4">
              <PhSparkle class="h-7 w-7 tio-fg-info" />
            </div>
            <Heading @level={{4}} class="mb-2">Upload Documents</Heading>
            <Text @size="sm" class="tio-fg-secondary mb-4">
              Upload your loan statements or NSLDS file for automatic data verification.
            </Text>
            <LinkTo
              @route="authenticated.observability.upload"
              @query={{hash
                source="reimagined-documents"
                providers="nslds,statement"
                returnTo=(if
                  this.isReimagined
                  "authenticated.reimagined.documents"
                  "authenticated.observability.upload"
                )
              }}
              class={{if
                this.isReimagined
                "block w-full rounded-md border tio-border-info-strong tio-bg-surface px-4 py-2 text-center tio-text-sm-medium tio-fg-info shadow-sm hover:tio-bg-info-soft-hover focus:outline-none focus-visible:ring-2 focus-visible:tio-ring-info focus-visible:ring-offset-2"
                "block w-full rounded-md border tio-border-info tio-bg-surface px-4 py-2 text-center tio-text-sm-medium tio-fg-info shadow-sm hover:tio-bg-info-soft focus:outline-none focus-visible:ring-2 focus-visible:tio-ring-info focus-visible:ring-offset-2"
              }}
            >
              Upload Documents
            </LinkTo>
          </Card>
        {{/if}}

        <Card @padding="lg" class="text-center">
          <div class="tio-row-center h-14 w-14 rounded-xl tio-bg-info-soft mx-auto mb-4">
            <PhRobot class="h-7 w-7 tio-fg-info" />
          </div>
          <Heading @level={{4}} class="mb-2">AI-Assisted</Heading>
          <Text @size="sm" class="tio-fg-secondary mb-4">
            Our AI reads and extracts data from your documents automatically.
          </Text>
          <Chip @intent="primary">Coming Soon</Chip>
        </Card>
      </div>

      <Drawer @isOpen={{this.isViewerOpen}} @onClose={{this.closeViewer}} @size="lg">
        <:header>
          <div class="tio-row">
            <div class="p-2 rounded-lg tio-bg-brand-soft">
              <PhFileText class="tio-icon-md tio-fg-brand" />
            </div>
            <div class="tio-fill-min">
              <Heading @level={{3}} class="truncate">
                {{#if this.selectedDocument}}
                  {{this.selectedDocument.name}}
                {{else}}
                  Document Details
                {{/if}}
              </Heading>
              {{#if this.selectedDocument}}
                <Text @size="sm" class="tio-fg-muted">{{this.selectedDocument.type}}</Text>
              {{/if}}
            </div>
            {{#if this.selectedDocument}}
              <Chip
                @intent={{if
                  (eq this.selectedDocument.status "verified")
                  "success"
                  (if (eq this.selectedDocument.status "pending") "warning" "danger")
                }}
              >
                {{this.selectedDocument.statusLabel}}
              </Chip>
            {{/if}}
          </div>
        </:header>
        <:body>
          {{#if this.selectedDocument}}
            <div class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-1">Upload Date</Text>
                  <Text @size="sm" @weight="medium">{{this.selectedDocument.uploadDate}}</Text>
                </div>
                <div>
                  <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-1">Document Type</Text>
                  <Text @size="sm" @weight="medium">{{this.selectedDocument.type}}</Text>
                </div>
                <div>
                  <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-1">Status</Text>
                  <Text @size="sm" @weight="medium">{{this.selectedDocument.statusLabel}}</Text>
                </div>
                <div>
                  <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-1">Document ID</Text>
                  <Text
                    @size="sm"
                    class="font-mono tio-fg-muted"
                  >{{this.selectedDocument.id}}</Text>
                </div>
              </div>

              {{#if this.selectedObservableDocument}}
                <div class="border-t tio-border-default pt-4 mt-4">
                  <Heading @level={{4}} @size="sm" class="mb-3">
                    Extracted Data
                  </Heading>
                  {{#if (eq this.selectedDocument.status "verified")}}
                    <ExtractionSummary
                      @document={{this.selectedObservableDocument}}
                      class="w-full"
                    />
                  {{else if (eq this.selectedDocument.status "pending")}}
                    <div class="tio-row p-4 tio-bg-warning-soft rounded-lg">
                      <PhClock class="tio-icon-md tio-fg-warning shrink-0" />
                      <p class="tio-fg-warning-body text-sm">
                        This document is being processed. Data will appear here once extraction is
                        complete.
                      </p>
                    </div>
                  {{else}}
                    <div class="tio-row p-4 tio-bg-danger-soft rounded-lg">
                      <PhWarning class="tio-icon-md tio-fg-danger shrink-0" />
                      <Text @size="sm" class="tio-fg-danger-body">
                        We couldn't extract data from this document. Please try uploading again or
                        contact support.
                      </Text>
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/if}}
        </:body>
        <:footer>
          <div class="tio-row">
            {{#if this.selectedObservableDocument}}
              <a
                href={{this.selectedObservableDocument.observableAsset.signedUrl}}
                target="_blank"
                rel="noopener noreferrer"
                class={{if
                  this.isReimagined
                  "tio-btn tio-btn--brand tio-btn--md"
                  "tio-inline-row gap-2 px-4 py-2 tio-bg-brand hover:tio-bg-brand-hover tio-fg-inverse rounded-lg transition-colors tio-text-sm-medium"
                }}
              >
                <PhDownloadSimple @size={{16}} />
                Download Original
                <PhArrowSquareOut @size={{14}} />
              </a>
            {{/if}}
            <Button
              @appearance="outlined"
              @size="sm"
              @label="Close"
              @testHook="document-center__close-viewer"
              {{on "click" this.closeViewer}}
            />
          </div>
        </:footer>
      </Drawer>
    </VStack>
  </template>
}
